import React, { PropsWithChildren, useEffect, useState } from 'react'

import { EuiCallOut, EuiInMemoryTable, EuiSearchBar, EuiSpacer } from '@elastic/eui'
import { EuiInMemoryTableProps } from '@elastic/eui/src/components/basic_table/in_memory_table'
import { EuiSearchBarOnChangeArgs, EuiSearchBarProps } from '@elastic/eui/src/components/search_bar/search_bar'

import { DEFAULT_INITIAL_PAGE_SIZE, DEFAULT_PAGE_SIZE_OPTIONS } from '../../utils/DefaultPageSize'

export type AcEuiInMemoryTableWithSearchProps<T> = {
  search: EuiSearchBarProps
  searchFields: string[]
  queryError?: Error | null
  initialPageSize?: number
  pageSizeOptions?: number[]
} & Omit<EuiInMemoryTableProps<T>, 'pagination'>

export function AcEuiInMemoryTableWithSearch<T>({ search, searchFields, queryError, initialPageSize, pageSizeOptions, ...props }: PropsWithChildren<AcEuiInMemoryTableWithSearchProps<T>>) {
  const [queryText, setQueryText] = useState('')
  const [error, setError] = useState<Error | null>(null)
  const [queriedItems, setQueriedItems] = useState(props.items)

  const searchOnChange = ({ queryText, error }: EuiSearchBarOnChangeArgs) => {
    if (error) {
      setError(error)
    } else {
      setError(null)
      setQueryText(queryText)
    }
  }

  useEffect(() => {
    if (search.onChange || !searchFields) {
      return
    }

    setQueriedItems(
      EuiSearchBar.Query.execute(queryText, props.items, {
        defaultFields: searchFields
      })
    )
  }, [search, searchFields, queryText, props.items])

  const pagination = {
    initialPageSize: initialPageSize ?? DEFAULT_INITIAL_PAGE_SIZE,
    pageSizeOptions: pageSizeOptions ?? DEFAULT_PAGE_SIZE_OPTIONS
  }

  const RenderContent = () => {
    const err = queryError ?? error

    if (err) {
      return (
        <>
          <EuiCallOut color='danger' title={`Invalid search: ${err.message}`} />
          <EuiSpacer size='l' />
        </>
      )
    }

    if (search.onChange || !searchFields) {
      return <EuiInMemoryTable {...props} pagination={pagination} responsive={false} />
    }

    return <EuiInMemoryTable {...props} pagination={pagination} items={queriedItems} responsive={false} />
  }

  return (
    <>
      <EuiSearchBar {...search} onChange={search.onChange ?? searchOnChange} />
      <EuiSpacer size='l' />
      <RenderContent />
    </>
  )
}
